import { createFileRoute, redirect } from "@tanstack/react-router";
import { AppLayout } from "../components/AppLayout";
import { useAuth } from "../hooks/useAuth";
import { Loader2 } from "lucide-react";

// Error fallback component for access errors
function HelpdeskAccessError({ error }: { error: Error }) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Access Error
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {error.message || "You do not have access to the helpdesk."}
        </p>
        <button
          onClick={() => (window.location.href = "/login")}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Go to Login
        </button>
      </div>
    </div>
  );
}

// Loading component
function HelpdeskLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
        <p className="text-gray-600 dark:text-gray-400">Loading helpdesk...</p>
      </div>
    </div>
  );
}

// Validate user has agent/admin access
function validateHelpdeskAccess() {
  const authToken = localStorage.getItem("authToken");
  if (!authToken) {
    throw redirect({ to: "/login" });
  }

  try {
    const payload = JSON.parse(atob(authToken.split(".")[1]));
    // Check for admin or agent role
    const roles = payload.role || payload.roles || [];
    const roleArray = Array.isArray(roles) ? roles : [roles];
    const hasAccess = roleArray.some((r: string) =>
      ["Admin", "Agent"].includes(r)
    );
    if (!hasAccess) {
      throw new Error("You must be an admin or agent to access the helpdesk");
    }
  } catch (e) {
    if (e instanceof Error && e.message.includes("admin or agent")) {
      throw e;
    }
    // Token parsing error - redirect to login
    throw redirect({ to: "/login" });
  }

  return { isAuthenticated: true };
}

// Layout component that wraps helpdesk routes (unified multi-org view)
function HelpdeskLayout() {
  const { isLoading } = useAuth();

  // Don't render layout until auth context is fully loaded
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // No PortalProvider needed - unified view doesn't depend on single org context
  return <AppLayout />;
}

export const Route = createFileRoute("/helpdesk")({
  beforeLoad: validateHelpdeskAccess,
  component: HelpdeskLayout,
  pendingComponent: HelpdeskLoading,
  errorComponent: ({ error }) => <HelpdeskAccessError error={error} />,
});
